import React, { useCallback, useContext, useEffect, useState } from "react";
import { Stepper, Step, StepLabel, Typography } from "@mui/material";
import {
  Connector,
  Content,
  ContinueButtonContainer,
  MultiStepFormStyled,
} from "./multistep-form.styles";
import { StepContentProps } from "./types";
import InitialDetails from "./initial-details/initial-details";
import { LightButton, PrimaryButton } from "../../../components/button/button";
import PetDetails from "./pet-details/pet-details";
import { StepperContext, StepperDataContext } from "../context/stepper-context";
import { useAppDispatch } from "../../../store/hooks";
import { addNotification } from "../../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import BookSlot from "./book-slot/book-slot";
import ManuallyScheduleModal from "./book-slot/manually-schedule-modal/manually-schedule-modal";
import AppointmentReview from "./appointment-review/appointment-review";
import { createAppointment, updatePatient } from "../../../actions";
import { AxiosError } from "axios";
import { useDialogs } from "@toolpad/core";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { isValidSelectedPatients, getSessionUser } from "../../../utils/common-utils";
import { useFormContext } from "react-hook-form";

const steps: string[] = [
  "Initial details",
  "Pet details",
  "Book time slot",
  "Review",
];

const StepContent: React.FC<StepContentProps> = ({ step }) => {
  switch (step) {
    case 0:
      return <InitialDetails />;
    case 1:
      return <PetDetails />;
    case 2:
      return <BookSlot />;
    case 3:
      return <AppointmentReview />;
    default:
      return <div>Unknown step</div>;
  }
};

const MultiStepForm: React.FC = () => {
  const { activeStep, setActiveStep } = useContext(StepperContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const {
    selectedClient,
    selectedPatients,
    selectedTimeSlot,
    selectedProvider,
    isRescheduleEnabled,
  } = useContext(StepperDataContext);
  const [modalState, setModalState] = useState<{
    addManually: boolean;
  }>({
    addManually: false,
  });
  const dispatch = useAppDispatch();
  const dialogs = useDialogs();
  const {
    watch,
    formState: { isValid },
  } = useFormContext();
  const formValues = watch();

  const navigate = useNavigate();

  const handleNext = async () => {
    if (!selectedClient) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: "Please select a client before proceeding.",
        })
      );
    } else {
      if (activeStep === 0 && typeof formValues?.address?.value === "object") {
        setIsLoading(true);
        await updatePatient({
          ...selectedClient,
          address: formValues?.address?.value,
        });
        setIsLoading(false);
      }
      if (activeStep < steps.length - 1) {
        if (activeStep === 1) {
          if (!isValidSelectedPatients(selectedPatients)) {
            dispatch(
              addNotification({
                id: uuid(),
                type: "error",
                message:
                  "Please select complete details of patient before proceeding.",
              })
            );
            return;
          }
        } else if (activeStep === 2) {
          if (!selectedTimeSlot) {
            dispatch(
              addNotification({
                id: uuid(),
                type: "error",
                message: "Please select or add a time slot before proceeding.",
              })
            );
            return;
          }
        }
        setActiveStep(activeStep + 1);
      } else {
        if (selectedClient && selectedPatients.length && selectedTimeSlot) {
          const confirmed = await dialogs.confirm(
            "Are you sure you want to perform this action?",
            {
              okText: "Yes",
              cancelText: "No",
            }
          );
          if (confirmed) {
            const payload = {
              provider: selectedProvider?.actor_id,
              clientId: selectedClient.integration_id,
              appointments: selectedPatients.map((selectedPatient) => {
                return {
                  patientId: selectedPatient.integration_id,
                  start: dayjs(selectedTimeSlot.start).format(
                    "YYYY-MM-DDTHH:mm:ssZZ"
                  ),
                  end: dayjs(selectedTimeSlot.end).format(
                    "YYYY-MM-DDTHH:mm:ssZZ"
                  ),
                  clientReason: selectedPatient.reason,
                  appointmentType: selectedPatient.appointment_type,
                  isManuallySelected: selectedTimeSlot.isManuallySelected,
                  prevTravel: selectedTimeSlot.prevTravel,
                };
              }),
              canReschedule: isRescheduleEnabled,
            };

            console.log("here is the payload", payload);
            createAppointment(payload)
              .then((response) => {
                const { message } = response.data;
                dispatch(
                  addNotification({
                    id: uuid(),
                    type: "success",
                    message: message,
                  })
                );
                navigate("/dashboard");
              })
              .catch((error: AxiosError) => {
                dispatch(
                  addNotification({
                    id: uuid(),
                    type: "error",
                    message: error.message,
                  })
                );
              });
          }
        } else {
          setIsLoading(false);
          dispatch(
            addNotification({
              id: uuid(),
              type: "error",
              message:
                "Please make sure all the required data is selected before proceeding.",
            })
          );
        }
      }
    }
  };

  const resetModalState = () =>
    setModalState((state) => ({ ...state, addManually: false }));
  const handleManualScheduleButton = () =>
    setModalState((state) => ({ ...state, addManually: true }));

  const checkIfDisabled = useCallback(() => {
    if (activeStep === 0) return !selectedClient || !isValid;
    if (activeStep === 1) return !isValidSelectedPatients(selectedPatients);
    if (activeStep === 2) return !selectedTimeSlot;
    return false;
  }, [activeStep, isValid, selectedClient, selectedPatients, selectedTimeSlot]);

  useEffect(() => {
    setIsDisabled(checkIfDisabled());
  }, [activeStep, checkIfDisabled, selectedPatients, selectedTimeSlot]);
  // Only allow manual scheduling for specific emails
  const { email: userEmail } = getSessionUser();
  const allowedManualEmails = ["brittanymarvel@gmail.com"];
  const canManuallySchedule = !!userEmail && allowedManualEmails.includes(userEmail);

  return (
    <MultiStepFormStyled>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        connector={<Connector />}
      >
        {steps.map((label, index) => (
          <Step key={index}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <div>
        {activeStep === steps.length ? (
          <Typography variant="h5">All steps completed</Typography>
        ) : (
          <Content>
            <StepContent step={activeStep} />
            <div className="bottom-button">
              {activeStep === 2 && (
                <ContinueButtonContainer>
                  {canManuallySchedule ? (
                    <LightButton onClick={handleManualScheduleButton}>
                      Manually schedule
                    </LightButton>
                  ) : (
                    <LightButton disabled>
                      Manually schedule
                    </LightButton>
                  )}
                </ContinueButtonContainer>
              )}
              <ContinueButtonContainer>
                <PrimaryButton
                  disabled={isDisabled}
                  onClick={handleNext}
                  isLoading={isLoading}
                >
                  {activeStep === 3 ? "Confirm booking" : "Continue"}
                </PrimaryButton>
              </ContinueButtonContainer>
            </div>
            <ManuallyScheduleModal
              open={modalState.addManually}
              onClose={resetModalState}
            />
          </Content>
        )}
      </div>
    </MultiStepFormStyled>
  );
};

export default MultiStepForm;
